import type { MouseEvent } from 'react'

import { cn } from '@/ui/lib/utils'
import { TONE_CLASS, type ReturnTone } from '@/ui/lib/quantFormat'

export interface QuantStrategyStat {
  label: string
  value: string
  /** Sign tone for return stats; omit for a neutral stat. */
  tone?: ReturnTone
  /** Accent (live) value. */
  accent?: boolean
}

export interface QuantStrategyTab {
  label: string
  href: string
  active: boolean
  /** Muted count shown after the label (`Runs 7`, `Live 1`). */
  count?: string
}

export interface QuantStrategyHeaderProps {
  name: string
  slug: string
  /** Stats are omitted for an invalid strategy. */
  stats?: QuantStrategyStat[]
  description?: string
  /**
   * Compact: no description paragraph and a fixed height (the run detail, spec
   * 038 R-2). The full description moves into the header's `title`.
   */
  compact?: boolean
  tabs: QuantStrategyTab[]
  onNavigate: (href: string) => void
}

/**
 * Header shared by a strategy's Config / Runs / Live panes (spec 036 handoff
 * 3.2): 16px caps name and slug, right-aligned stats, a description (max
 * 760px) and the tab bar. Tabs are real links; presentational only.
 */
export function QuantStrategyHeader({ name, slug, stats, description, compact = false, tabs, onNavigate }: QuantStrategyHeaderProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <div
      title={compact ? description : undefined}
      className={cn(
        'flex shrink-0 flex-col gap-2.5 border-b border-paper-14 px-7 pt-[18px]',
        compact && 'h-[104px] overflow-hidden',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2
          className="m-0 max-w-full truncate font-sans text-[16px] font-bold tracking-[0.16em] uppercase"
          title={name}
        >
          {name}
        </h2>
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-paper-40" title={slug}>
          {slug}
        </span>
        {stats && (
          <dl className="m-0 flex gap-[26px]">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col items-end gap-0.5">
                <dt className="font-mono text-[9px] tracking-[0.16em] text-paper-40 uppercase">{stat.label}</dt>
                <dd
                  className={cn(
                    'm-0 font-mono text-[13px]',
                    stat.tone !== undefined
                      ? TONE_CLASS[stat.tone]
                      : stat.accent
                        ? 'text-accent-line'
                        : 'text-paper-90',
                  )}
                >
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
      {!compact && description && (
        <div className="max-w-[760px] font-mono text-[12.5px] leading-[1.55] text-paper-50 [text-wrap:pretty]">
          {description}
        </div>
      )}
      <nav aria-label="Strategy sections" className="-ml-3.5 flex h-[38px] items-stretch">
        {tabs.map((tab) => (
          <a
            key={tab.href}
            href={tab.href}
            onClick={(event) => handleClick(event, tab.href)}
            aria-current={tab.active ? 'page' : undefined}
            className={cn(
              '-mb-px flex items-center border-b-2 px-3.5 font-sans text-[10.5px] tracking-[0.2em] uppercase',
              tab.active
                ? 'border-accent-line font-bold text-paper'
                : 'border-transparent font-semibold text-paper-50',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-[7px] font-mono font-normal tracking-[0.06em] opacity-60">{tab.count}</span>
            )}
          </a>
        ))}
      </nav>
    </div>
  )
}
